import { useEffect, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { PageHeader, EmptyState, fmtDateTime } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { DownloadSimple, FloppyDisk, ArrowClockwise, ArrowsClockwise, DropboxLogo, CheckCircle, WarningCircle, ArrowCounterClockwise, UploadSimple, Warning } from "@phosphor-icons/react";

function DropboxCard() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/dropbox/status").then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const flag = new URLSearchParams(window.location.search).get("dropbox");
    if (flag === "connected") toast.success("Dropbox connected — backups will now be copied to your Dropbox");
    else if (flag) toast.error(`Dropbox connection failed (${flag.replace("_", " ")})`);
    if (flag) window.history.replaceState({}, "", "/backups");
  }, []);
  const connect = async () => {
    setBusy(true);
    try { const { data } = await api.get("/dropbox/connect"); window.location.href = data.url; }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); setBusy(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect Dropbox? Backups will no longer be copied there.")) return;
    await api.post("/dropbox/disconnect"); toast.success("Dropbox disconnected"); load();
  };
  const test = async () => {
    setBusy(true);
    try { const { data } = await api.post("/dropbox/test"); toast.success(`Test file written to Dropbox: ${data.path}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
    finally { setBusy(false); }
  };
  const backupNow = async () => {
    setBusy(true);
    try {
      await api.post("/backups/run");
      toast.success("Full portal backup started — a complete copy is being saved to your Dropbox. This can take a moment.");
      setTimeout(load, 6000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start backup"); }
    finally { setTimeout(() => setBusy(false), 6000); }
  };
  if (!st) return null;
  return (
    <div className="hp-card p-5 flex flex-wrap items-center gap-4" data-testid="dropbox-card">
      <div className="h-11 w-11 rounded-sm bg-[#0061FF]/10 text-[#0061FF] flex items-center justify-center shrink-0"><DropboxLogo size={26} weight="fill" /></div>
      <div className="flex-1 min-w-[220px]">
        <p className="font-semibold">Dropbox off-site copy</p>
        {st.connected ? (
          <p className="text-sm text-muted-foreground flex items-center gap-1.5 flex-wrap" data-testid="dropbox-status">
            <CheckCircle size={14} weight="fill" className="text-emerald-600" /> Connected{st.account ? ` as ${st.account.name} (${st.account.email})` : ""} · files go to <code className="text-xs">Apps/…{st.folder}/</code>
            {st.last_upload_at && <span>· last copy {fmtDateTime(st.last_upload_at)}</span>}
          </p>
        ) : (
          <p className="text-sm text-muted-foreground" data-testid="dropbox-status">Not connected. Every backup can also be copied automatically to a folder in your Dropbox.</p>
        )}
        {st.last_error && <p className="text-xs text-red-600 mt-1 flex items-center gap-1" data-testid="dropbox-error"><WarningCircle size={14} /> Last error: {st.last_error}</p>}
        {!st.connected && st.redirect_uri && <p className="text-xs text-muted-foreground mt-1">Before connecting, add this Redirect URI in your Dropbox app settings: <code className="text-xs break-all">{st.redirect_uri}</code></p>}
        {st.connected && (st.needs_reconnect || !st.scope_known) && (
          <div className="mt-2 rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="dropbox-reconnect-hint">
            <p className="font-semibold">Extra permission needed for reading photo folders</p>
            <p className="mt-0.5">To let the PTN card read batch &amp; expiry from a shared Dropbox photos link, Dropbox must grant the <code>sharing.read</code> permission. In your Dropbox App Console → Permissions, tick <strong>sharing.read</strong> and Submit, then click <strong>Reconnect</strong> here and approve again.</p>
          </div>
        )}
      </div>
      <div className="flex gap-2 flex-wrap">
        {st.connected ? (
          <>
            <Button className="rounded-sm bg-[#0061FF] hover:bg-[#0052d9]" onClick={backupNow} disabled={busy} data-testid="dropbox-backup-now"><FloppyDisk size={16} className="mr-2" weight="fill" /> Back up to Dropbox now</Button>
            <Button variant="outline" className="rounded-sm" onClick={test} disabled={busy} data-testid="dropbox-test">Send test file</Button>
            <Button variant="outline" className="rounded-sm" onClick={connect} disabled={busy} data-testid="dropbox-reconnect"><ArrowsClockwise size={16} className="mr-2" /> Reconnect</Button>
            <Button variant="outline" className="rounded-sm text-red-600" onClick={disconnect} data-testid="dropbox-disconnect">Disconnect</Button>
          </>
        ) : (
          <Button className="rounded-sm bg-[#0061FF] hover:bg-[#0052d9]" onClick={connect} disabled={busy || !st.configured} data-testid="dropbox-connect"><DropboxLogo size={16} className="mr-2" weight="fill" /> Connect Dropbox</Button>
        )}
      </div>
    </div>
  );
}

function ConfirmRestore({ state, onClose, onDone }) {
  const [txt, setTxt] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setTxt(""); }, [state]);
  if (!state) return null;
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await state.action();
      const total = Object.values(data.restored || {}).reduce((a, n) => a + n, 0);
      toast.success(`Data restored successfully from ${data.source} — ${total.toLocaleString()} records loaded`);
      onDone();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="restore-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="flex items-center gap-2 text-red-600"><Warning size={20} weight="fill" /> {state.title}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-muted-foreground">{state.description}</p>
          <div className="rounded-sm border border-blue-200 bg-blue-50 p-3 text-blue-700 text-xs">This <b>restores only the records that are missing</b> from the system. Your current data is kept — nothing is wiped or overwritten.</div>
          <div>
            <label className="text-xs font-medium">Type <b>RESTORE</b> to confirm</label>
            <Input value={txt} onChange={(e) => setTxt(e.target.value)} placeholder="RESTORE" data-testid="restore-confirm-input" className="mt-1 rounded-sm" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={onClose} disabled={busy} data-testid="restore-cancel">Cancel</Button>
          <Button className="rounded-sm" disabled={txt !== "RESTORE" || busy} onClick={run} data-testid="restore-confirm-submit">{busy ? "Restoring…" : "Restore Missing Data"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DropboxRestorePicker({ open, onClose, onPick }) {
  const [folders, setFolders] = useState(null);
  useEffect(() => {
    if (!open) return;
    setFolders(null);
    api.get("/dropbox/backups").then((r) => setFolders(r.data.connected ? (r.data.folders || []) : "off")).catch(() => setFolders([]));
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="dropbox-restore-picker" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="flex items-center gap-2"><DropboxLogo size={20} weight="fill" className="text-[#0061FF]" /> Restore from Dropbox</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-80 overflow-auto">
          {folders === null ? <p className="text-sm text-muted-foreground">Loading backups from Dropbox…</p>
            : folders === "off" ? <p className="text-sm text-muted-foreground">Dropbox isn’t connected. Connect it above first.</p>
            : folders.length === 0 ? <p className="text-sm text-muted-foreground">No backups found in your Dropbox yet.</p>
            : folders.map((f) => (
              <button key={f} onClick={() => onPick(f)} data-testid={`dropbox-restore-${f}`}
                className="w-full text-left border border-border rounded-sm p-3 hover:bg-muted/50 flex items-center justify-between">
                <span className="font-mono text-sm">{f}</span>
                <ArrowCounterClockwise size={16} className="text-red-600" />
              </button>
            ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Backups() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [fileKey, setFileKey] = useState(0);
  const [dropboxOpen, setDropboxOpen] = useState(false);
  const [restoreState, setRestoreState] = useState(null);

  const load = () => Promise.all([api.get("/backups").then((r) => setRows(r.data)), api.get("/backups/health").then((r) => setHealth(r.data))]).catch(() => {}).finally(() => setLoading(false));
  const [health, setHealth] = useState(null);
  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      await api.post("/backups/run");
      toast.success("Backup started — refreshing shortly…");
      setTimeout(load, 4000);
    } catch { toast.error("Could not start backup"); }
    finally { setTimeout(() => setRunning(false), 4000); }
  };

  const download = (f) => {
    const token = localStorage.getItem("token");
    window.open(`${BACKEND_URL}/api/files/${f.storage_path}?auth=${token}&download=1`, "_blank");
  };

  return (
    <div className="space-y-6" data-testid="backups-page">
      <PageHeader
        subtitle="Data protection"
        title="Backups & Exports"
        actions={
          <Button className="rounded-sm" onClick={runNow} disabled={running} data-testid="run-backup-now">
            <ArrowClockwise size={16} className={`mr-2 ${running ? "animate-spin" : ""}`} /> Run backup now
          </Button>
        }
      />

      {health && (
        <div className={`rounded-md border px-4 py-3 text-sm flex flex-wrap items-center gap-x-4 gap-y-1 ${health.healthy ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-amber-50 border-amber-200 text-amber-900"}`} data-testid="backup-health">
          {health.healthy ? <CheckCircle size={18} weight="fill" className="text-emerald-600" /> : <WarningCircle size={18} weight="fill" className="text-amber-600" />}
          <span className="font-semibold">{health.healthy ? "Backups healthy" : "Backups need attention"}</span>
          <span>Last backup: {health.last_backup_at ? `${fmtDateTime(health.last_backup_at)} (${health.last_backup_age_hours} h ago)` : "never"}</span>
          <span>Schedule: {health.schedule}</span>
          <span>Dropbox: {health.last_dropbox_status === "ok" ? "uploaded" : health.last_dropbox_status === "error" ? `error — ${health.dropbox?.last_error || ""}` : "not connected"}</span>
          {health.files_sync && <span data-testid="backup-files-sync">Documents mirrored to Dropbox: {health.files_sync.uploaded} new{health.files_sync.remaining > 0 ? `, ${health.files_sync.remaining} pending` : ", all up to date"}{health.files_sync.status === "error" ? ` — ${health.files_sync.error}` : ""}</span>}
          <span data-testid="backup-collections-count">{health.collections?.length} data collections covered</span>
          {health.stuck_runs > 0 && <span>{health.stuck_runs} run(s) never finished</span>}
          {health.recent_failures?.[0] && <span className="basis-full text-xs" data-testid="backup-last-failure">Last failure {fmtDateTime(health.recent_failures[0].at)}: {health.recent_failures[0].error}</span>}
        </div>
      )}
      <p className="text-sm text-muted-foreground -mt-2">
        Automatic backups run twice a day at 02:00 and 14:00 UTC. Each backup includes a full database snapshot (JSON) of every data collection — orders, shipments, invoices, quotes, stock, dispatch, clients, users, settings, FedEx pickups, aliases, audit log and more — plus Invoices, Orders, Shipments, Stock and a documents-manifest export (CSV). Only the last 7 days of backups are kept — older copies are removed automatically from storage and from Dropbox to save space. When Dropbox is connected, every automatic backup is also copied there, and all stored documents (goods photos, AWBs, labels, invoice PDFs) are mirrored once into the Dropbox <code className="text-xs">files/</code> folder.
      </p>

      <DropboxCard />

      <div className="hp-card p-5 space-y-3" data-testid="restore-card">
        <div className="flex items-center gap-2">
          <ArrowCounterClockwise size={18} className="text-red-600" weight="bold" />
          <p className="font-semibold">Recover data</p>
        </div>
        <p className="text-sm text-muted-foreground">
          If anything happens to your data, restore from a backup — a saved snapshot below, a backup file you download, or a copy saved on Dropbox. Restoring only adds back records that are missing; it never wipes or overwrites what's already in the system.
        </p>
        <div className="flex flex-wrap gap-3 items-center pt-1">
          <input key={fileKey} type="file" accept="application/json,.json" onChange={(e) => setUploadFile(e.target.files?.[0] || null)} data-testid="restore-file-input" className="text-sm max-w-[240px]" />
          <Button variant="outline" className="rounded-sm text-red-600" disabled={!uploadFile} data-testid="restore-from-file"
            onClick={() => setRestoreState({ title: "Restore from file", description: `Restore the database from “${uploadFile.name}”.`, action: () => { const fd = new FormData(); fd.append("confirm", "RESTORE"); fd.append("file", uploadFile); return api.post("/backups/restore-upload", fd); } })}>
            <UploadSimple size={16} className="mr-1.5" /> Restore from file
          </Button>
          <Button variant="outline" className="rounded-sm text-[#0061FF]" data-testid="restore-from-dropbox" onClick={() => setDropboxOpen(true)}>
            <DropboxLogo size={16} className="mr-1.5" weight="fill" /> Restore from Dropbox
          </Button>
        </div>
      </div>

      {loading ? null : rows.length === 0 ? (
        <EmptyState
          title="No backups yet"
          hint="The first automatic backup runs at the next 02:00 or 14:00 UTC slot — or click ‘Run backup now’ to create one immediately."
          action={<Button className="rounded-sm" onClick={runNow}><FloppyDisk size={16} className="mr-2" /> Run backup now</Button>}
        />
      ) : (
        <div className="space-y-3">
          {rows.map((b) => {
            const totalDocs = Object.values(b.counts || {}).reduce((a, n) => a + n, 0);
            return (
              <div key={b.id} className="hp-card p-4" data-testid={`backup-row-${b.ts}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-heading font-semibold flex items-center gap-2">
                      <FloppyDisk size={16} className="text-primary" weight="duotone" />
                      {fmtDateTime(b.generated_at)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {totalDocs} records · {b.counts?.orders || 0} orders · {b.counts?.invoices || 0} invoices
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {(b.files || []).map((f) => (
                      <Button key={f.storage_path} variant="outline" size="sm" className="rounded-sm" onClick={() => download(f)} data-testid={`backup-download-${f.label}`}>
                        <DownloadSimple size={14} className="mr-1.5" /> {f.label}
                      </Button>
                    ))}
                    <Button variant="outline" size="sm" className="rounded-sm text-red-600" data-testid={`backup-restore-${b.ts}`}
                      onClick={() => setRestoreState({ title: "Restore this backup", description: `Restore the snapshot from ${fmtDateTime(b.generated_at)}.`, action: () => api.post(`/backups/${b.id}/restore`, { confirm: "RESTORE" }) })}>
                      <ArrowCounterClockwise size={14} className="mr-1.5" /> Restore
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <DropboxRestorePicker open={dropboxOpen} onClose={() => setDropboxOpen(false)}
        onPick={(ts) => { setDropboxOpen(false); setRestoreState({ title: "Restore from Dropbox", description: `Restore the snapshot “${ts}” from your Dropbox.`, action: () => api.post("/backups/restore-dropbox", { confirm: "RESTORE", ts }) }); }} />
      <ConfirmRestore state={restoreState} onClose={() => setRestoreState(null)} onDone={() => { load(); setUploadFile(null); setFileKey((k) => k + 1); }} />
    </div>
  );
}
